import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { sec } from "../utils/security";
import { useApiConfiguration } from "../hooks/useApiConfiguration";
import { useHostAdapter } from "../platform";
import { useAddInVersion } from "../hooks/useAddInVersion";
import PaneHeader from "../components/layouts/PaneHeader";
import HeaderMenu from "../components/layouts/HeaderMenu";
import PaneStatus from "../components/feedback/PaneStatus";
import JyloLogo from "../components/layouts/JyloLogo";
import { Button } from "../components/ui/button";

type AuthGateProps = {
  children: React.ReactNode;
};

/**
 * The sign-in screen. Everything else in this file is a waiting state; this is the
 * one screen a signed-out user actually looks at, so it gets the logo and a sentence
 * about what signing in is for.
 */
const SignIn = ({ onSignIn }: { onSignIn: () => void }) => {
  const host = useHostAdapter();
  const version = useAddInVersion();

  return (
    <div className="flex h-screen flex-col">
      <div className="flex flex-1 flex-col items-center justify-center gap-7 px-7 py-8 text-center">
        <JyloLogo height={30} />
        <div className="flex flex-col items-center gap-2">
          <h1 className="text-h5 text-strong">Sign in to Jylo</h1>
          <p className="max-w-[260px] text-body2 text-muted-foreground">
            Draft with the assistant, build assemblies, and save or check in documents you have
            checked out from Jylo.
          </p>
        </div>
        <div className="flex w-full max-w-[280px] flex-col gap-2.5">
          <Button size="lg" className="w-full" onClick={onSignIn}>
            Sign in
          </Button>
          <span className="text-caption text-muted-foreground">
            Opens your organisation&apos;s sign-in in a new window.
          </span>
        </div>
      </div>
      <div className="flex-none pb-3.5 text-center text-caption text-disabled">
        Jylo for {host.displayName}
        {version ? ` · v${version}` : ""}
      </div>
    </div>
  );
};

/** A header with only the overflow menu, over a status. For the states after sign-in but before the app. */
const Waiting = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-screen flex-col">
    <PaneHeader>
      <HeaderMenu />
    </PaneHeader>
    {children}
  </div>
);

const AuthGate = ({ children }: AuthGateProps) => {
  const { isAuthenticated, isLoading, loginWithPopup, getAccessTokenSilently, user } = useAuth0();
  const {
    isLoading: isConfiguringApi,
    isConfigured: isApiConfigured,
    error: apiConfigError,
    refreshConfiguration,
  } = useApiConfiguration();

  // Initialize security functions
  useEffect(() => {
    sec.setAccessTokenSilently(getAccessTokenSilently);
    sec.setGetUser(() => user);
  }, [getAccessTokenSilently, user]);

  if (isLoading) {
    return (
      <div className="flex h-screen flex-col">
        <PaneStatus fill loading title="Signing you in…" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <SignIn onSignIn={() => void loginWithPopup()} />;
  }

  if (isConfiguringApi) {
    return (
      <Waiting>
        <PaneStatus fill loading title="Connecting to Jylo…" />
      </Waiting>
    );
  }

  if (apiConfigError) {
    return (
      <Waiting>
        <PaneStatus
          fill
          tone="error"
          title="Could not connect to Jylo"
          description={apiConfigError}
          action={
            <Button variant="outline" onClick={refreshConfiguration}>
              Try again
            </Button>
          }
        />
      </Waiting>
    );
  }

  if (isApiConfigured) {
    return <>{children}</>;
  }

  return null;
};

export default AuthGate;
